import { Button, IconButton, Popover, Typography, useTranslate } from '@prenuvo/halo';
import { ResetView } from '@prenuvo/halo-icon';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { useStudyGuard } from '@/shared/guard';

/**
 * Reset Report Button
 *
 * Temporary button to reset the report to the initial state.
 * This is a temporary solution until we have a proper way to reset the report.
 */
export function ResetReportButton() {
  const queryClient = useQueryClient();
  const {
    radiologistScan: { patientID },
    studyId,
  } = useStudyGuard();

  const translate = useTranslate();

  const { data } = api.apps.study.query.useGetStudies(
    { payload: { params: { status: 'scanned', users: [patientID] } } },
    { enabled: import.meta.env.MODE === 'prd' },
  );

  const isTestStudy =
    data?.studies.find((study) => study.studyID === studyId)?.status.test ?? false;

  const { mutate: reportMutation } = api.fct.report.mutation.usePatchReport(queryClient);

  const handleResetReport = () => {
    reportMutation(
      {
        payload: {
          status: 'deprecated',
          studyId: studyId!,
        },
      },
      {
        onSuccess: () => {
          globalThis.location.reload();
        },
      },
    );
  };

  if (import.meta.env.MODE === 'prd' && !isTestStudy) {
    return null;
  }

  return (
    <div>
      <Popover
        trigger={
          <IconButton
            aria-label={translate('FCT.REPORTING.HEADER.ACTIONS.RESET_REPORT')}
            className={{ root: 'shrink-0' }}
            data-testid="reset-report-button"
            icon={ResetView}
            size="sm"
            variant="text"
          />
        }
      >
        <Typography as="p" translationKey="FCT.REPORTING.HEADER.RESET_REPORT.DESCRIPTION" />
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleResetReport}
            size="sm"
            translationKey="FCT.REPORTING.HEADER.ACTIONS.RESET_REPORT"
            variant="outlined"
          />
        </div>
      </Popover>
    </div>
  );
}
